/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import mbpLogger from '@module-logger/mbp';
import { object, string } from 'prop-types';

import { getFeatureFlag } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import findRecentlyViewedProducts from '../../../../gql/queries/findRecentlyViewedProducts';
import { getBrandName } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import useGetAccessTokenSafely from '../../../../gql/hooks/useGetAccessTokenSafely';
import { GRAPHQL_ENV } from '../../../../gql';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import CarouselBuilderSkeleton from '../../GraphqlSkeletonComponents/CarouselBuilderSkeleton';
import useBrowserUUID from '../../../../helpers/useBrowserUUID';
import { getIsBot } from '../../../../../state/ducks/App/App-Selectors';
import ComponentErrorBoundary from '../Common/ComponentErrorBoundary/ComponentErrorBoundary';
import PersonalizedHomePageCarouselBuilder from '../../GraphqlHomePage/Partials/PersonalizedHomePageCarouselBuilder/PersonalizedHomePageCarouselBuilder';
import SimpleCarouselBuilder from '../Common/SimpleCarouselBuilder';
import PersonalizedHomePageGridUIV2 from '../../GraphqlHomePage/Partials/PersonalizedHomePageGridUI/PersonalizedHomePageGridUIV2';
import RecentlyViewedGridUIV2 from '../RecentlyViewedProducts/RecentlyViewedGridUI/RecentlyViewedGridUIV2';

const RecentlyViewedRecommendation = ({ content, layoutDisplayType }) => {
    const isBot = useSelector(getIsBot);
    const browserUUID = useBrowserUUID();
    const brand = useSelector(getBrandName);
    const jwt = useGetAccessTokenSafely();
    const isPersonalizationHoldoutEnabled = useSelector(getFeatureFlag('is-personalization-holdout-enabled'));
    const ffIsPersonalizedHomePageProductEnabled = useSelector(getFeatureFlag('is-personalized-hp-product-enabled'));
    const dispatch = useDispatch();

    const productStylingConfig = content?.personalized_product_card_reference?.length > 0 ? content?.personalized_product_card_reference?.[0] : content;
    const personalizedCarousel = content?.personalized_carousel;
    const products_to_show = personalizedCarousel?.products_to_show?.desktop ? personalizedCarousel.products_to_show : { mobile: 2, tablet: 3, desktop: 5 };

    const {
        loading, error, data,
    } = useQuery(findRecentlyViewedProducts(), {
        variables: {
            brand,
            environment: GRAPHQL_ENV,
            isHoldoutEligible: isPersonalizationHoldoutEnabled,
            user: {
                anonymousId: browserUUID,
            },
        },
        context: {
            api: 'personalizedContent',
            jwt,
        },
        skip: isBot || !jwt || !browserUUID,
        fetchPolicy: 'network-only',
    });

    const products = data?.recentlyViewed?.products?.length > 0 ? data.recentlyViewed.products : [];

    useEffect(() => {
        const hasProducts = products?.length > 0;

        if (isBot || loading || error || !hasProducts || (content?.minimum_products_to_show || 0) > products.length) return;

        if (content?.ga_tagging?.event_category && content?.ga_tagging?.event_label) {
            dispatch(trackEvent({
                eventCategory: content?.ga_tagging?.event_category,
                eventAction: 'Impression',
                eventLabel: content?.ga_tagging?.event_label,
            }));
        }
    }, [isBot, loading, error, products, content, dispatch]);

    if (isBot || !jwt || !browserUUID) {
        return null;
    }

    if (loading) {
        return (
            <CarouselBuilderSkeleton
                nDisplayProducts={products_to_show}
                breakPoints={{ mobile: 601, desktop: 769 }}
            />
        );
    }

    if (error) {
        mbpLogger.logError({
            function: 'RecentlyViewedRecommendation.js',
            message: 'Error loading data for recently viewed products',
            appName: process.env.npm_package_name,
            module: 'RecentlyViewedRecommendation',
            error,
        });
        return null;
    }

    if (products.length === 0 || (content?.minimum_products_to_show || 0) > products.length) {
        return null;
    }

    const handleClick = (partNumber) => {
        if (content?.ga_tagging?.event_category && content?.ga_tagging?.event_label) {
            dispatch(trackEvent({
                eventCategory: content?.ga_tagging?.event_category,
                eventAction: 'Click',
                eventLabel: content?.ga_tagging?.event_label,
                eventName: 'feature_interaction',
                feature_category: content?.ga_tagging?.event_category,
                feature_element: 'Click',
                feature_detail: `${content?.ga_tagging?.event_label} - ${partNumber}`,
            }));
        }
    };

    const {
        main_title, subtitle, show_subtitle, title_styling,
    } = productStylingConfig;

    const renderCarousel = (containerPadding) => (
        <ComponentErrorBoundary function="RecentlyViewedRecommendation" module="PersonalizedContent" message="Something went wrong when displaying Personalized HP Recommendations from Salesforce">
            <PersonalizedHomePageCarouselBuilder
                blockData={{
                    productsToShow: products_to_show,
                    title: productStylingConfig?.main_title,
                    titleMargin: productStylingConfig?.title_styling?.title_margin,
                    titleFontColor: productStylingConfig?.title_styling?.title_font_color?.color,
                    titleFontSize: productStylingConfig?.title_styling?.title_font_size,
                    titleFontStyle: productStylingConfig?.title_styling?.title_font_style,
                    titleFontFamily: productStylingConfig?.title_styling?.title_font_family,
                    alignTitle: productStylingConfig?.title_styling?.align_title,
                    padding: productStylingConfig?.title_styling?.padding,
                    containerPadding: content?.padding,
                }}
                productClickCallback={(partNumber) => handleClick(partNumber)}
                products={products}
                demandType="XS"
                productStylingConfig={productStylingConfig}
                dataTestId="homepage-recently-viewed"
            />
        </ComponentErrorBoundary>
    );

    const renderSimpleCarousel = () => (
        <ComponentErrorBoundary function="RecentlyViewedRecsSFContainer" module="PersonalizedContent" message="Something went wrong when displaying Recently Viewed Recommendations from Salesforce">
            <SimpleCarouselBuilder
                blockData={{
                    productsToShow: products_to_show,
                    title: productStylingConfig?.main_title,
                    css_classes: content?.css_classes,
                    titleMargin: productStylingConfig?.title_styling?.title_margin,
                    titleFontColor: productStylingConfig?.title_styling?.title_font_color?.color,
                    titleFontSize: productStylingConfig?.title_styling?.title_font_size,
                    titleFontStyle: productStylingConfig?.title_styling?.title_font_style,
                    titleFontFamily: productStylingConfig?.title_styling?.title_font_family,
                    alignTitle: productStylingConfig?.title_styling?.align_title,
                    padding: productStylingConfig?.title_styling?.padding,
                }}
                productClickCallback={(partNumber) => handleClick(partNumber)}
                products={products}
                demandType="XS"
                dataTestId="homepage-recently-viewed"
            />
        </ComponentErrorBoundary>
    );

    if (ffIsPersonalizedHomePageProductEnabled) {
        if (layoutDisplayType === 'GRID') {
            return (
                <ComponentErrorBoundary function="PersonalizedHomePageGridUIV2" module="PersonalizedContent" message="Something went wrong when displaying Personalized HP Recommendations from recently viewed products">
                    <PersonalizedHomePageGridUIV2
                        products={products}
                        cmsData={{
                            ...title_styling,
                            title: main_title,
                            subTitle: subtitle,
                            min_products_to_show: content?.minimum_products_to_show || 0,
                            padding: personalizedCarousel?.padding,
                        }}
                        isNewDesign={false}
                        handleClickCallback={handleClick}
                        productStylingConfig={productStylingConfig}
                        personalizedCarousel={personalizedCarousel}
                        dataTestId="homepage-recently-viewed"
                    />
                </ComponentErrorBoundary>
            );
        }

        return renderCarousel();
    }
    if (layoutDisplayType === 'GRID') {
        return (
            <ComponentErrorBoundary function="RecentlyViewedGridUIV2" module="PersonalizedContent" message="Something went wrong when displaying Personalized HP Recommendations from recently viewed products">
                <RecentlyViewedGridUIV2
                    products={products}
                    cmsData={{
                        ...title_styling,
                        title: main_title,
                        subTitle: subtitle,
                        min_products_to_show: content?.minimum_products_to_show || 0,
                        padding: personalizedCarousel?.padding,
                        minimum_rows_to_show: content?.minimum_rows_to_show,
                        title_font_family: productStylingConfig?.title_styling?.title_font_family,
                        title_font_size: productStylingConfig?.title_styling?.title_font_size,
                        title_font_style: productStylingConfig?.title_styling?.title_font_style,
                        title_font_color: productStylingConfig?.title_styling?.title_font_color,
                        background_color: productStylingConfig?.background_color,
                        primary_grid_background_color: productStylingConfig?.primary_grid_background_color,
                        secondary_grid_background_color: productStylingConfig?.secondary_grid_background_color,
                    }}
                    personalizedCarousel={personalizedCarousel}
                    isNewDesign
                    handleClickCallback={handleClick}
                    dataTestId="homepage-recently-viewed"
                />;
            </ComponentErrorBoundary>
        );
    }

    return renderSimpleCarousel();
};

RecentlyViewedRecommendation.propTypes = {
    content: object.isRequired,
    layoutDisplayType: string.isRequired,
};

export default RecentlyViewedRecommendation;
